import * as React from "react";
import { cn } from "./cn.js";

export type BadgeTone =
  | "neutral"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "progress"
  | "outline";

const TONES: Record<BadgeTone, string> = {
  neutral: "bg-secondary text-secondary-foreground border-transparent",
  success: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] border-transparent",
  warning: "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] border-transparent",
  danger: "bg-destructive text-destructive-foreground border-transparent",
  info: "bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))] border-transparent",
  // `progress` is deliberately hollow with a dashed edge: "in flight", not "broken".
  progress:
    "border-dashed border-[hsl(var(--info))] text-[hsl(var(--info))] bg-[hsl(var(--info))]/10",
  outline: "border-border text-foreground bg-transparent",
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** A decorative glyph. Colour must never be the only signal, so pass one. */
  icon?: React.ReactNode;
}

export function Badge({ className, tone = "neutral", icon, children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5",
        "text-xs font-semibold leading-tight",
        "[&_svg]:size-3 [&_svg]:shrink-0",
        TONES[tone],
        className,
      )}
      {...props}
    >
      {icon ? (
        <span aria-hidden="true" className="inline-flex">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}
